import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Mail, Package, Smartphone, Shield, ChevronDown, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function WMSLogin() {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp]         = useState('');
  const [step, setStep]         = useState('credentials'); // 'credentials' | '2fa'
  const [showGuide, setShowGuide] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);

  const loadQR = async () => {
    if (qrData) return; // already loaded
    setQrLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/wms/auth/setup-qr`);
      setQrData(r.data);
    } catch { setQrData({ error: true }); }
    setQrLoading(false);
  };
  const [tempToken, setTempToken] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [lockoutMsg, setLockoutMsg] = useState('');
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setLockoutMsg('');
    try {
      await axios.post(`${API_URL}/api/wms/auth/seed`).catch(() => {});
      const res = await axios.post(`${API_URL}/api/wms/auth/login`, {
        email, password,
        ...(step === '2fa' ? { totp_code: totp } : {})
      });

      // 2FA required — move to second step
      if (res.data.requires_2fa) {
        setTempToken(res.data.temp_token || '');
        setStep('2fa');
        setLoading(false);
        return;
      }

      // Full login success
      localStorage.setItem('wms_token', res.data.token);
      localStorage.setItem('wms_user', JSON.stringify({
        email: res.data.email, name: res.data.name, role: res.data.role
      }));
      navigate('/wms');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object' && detail?.error === 'account_locked') {
        setLockoutMsg(`Account locked. Too many failed attempts. Try again after ${new Date(detail.unlock_at).toLocaleTimeString()}`);
      } else if (typeof detail === 'object' && detail?.error === 'invalid_2fa') {
        setError('Invalid authenticator code. Try again.');
      } else if (typeof detail === 'object') {
        const remaining = detail?.attempts_remaining;
        setError(detail?.message || 'Login failed' + (remaining !== undefined ? ` (${remaining} attempts left)` : ''));
      } else {
        setError(detail || 'Login failed');
      }
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
              <Package className="w-7 h-7 text-white" />
            </div>
            <div className="text-left">
              <h1 className="text-2xl font-bold text-white">Agora WMS</h1>
              <p className="text-blue-300 text-sm">Warehouse Management System</p>
            </div>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 shadow-2xl">
          {step === 'credentials' ? (
            <>
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-300" />Sign in to your account
              </h2>
              {lockoutMsg && (
                <div className="bg-orange-500/20 border border-orange-500/50 text-orange-200 px-4 py-3 rounded-lg mb-4 text-sm">
                  🔒 {lockoutMsg}
                </div>
              )}
              {error && <div className="bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-2 rounded-lg mb-4 text-sm">{error}</div>}
              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <label className="text-sm text-blue-200 mb-1.5 block">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                      autoComplete="username1" name="wms-email"
                      className="w-full bg-white/10 border border-white/20 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      placeholder="demo@agora-sg" data-testid="wms-login-email" />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-blue-200 mb-1.5 block">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
                    <input type="password" value={password} onChange={e => setPassword(e.target.value)} required
                      autoComplete="new-password1" name="wms-password"
                      className="w-full bg-white/10 border border-white/20 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      placeholder="••••••••" data-testid="wms-login-password" />
                  </div>
                </div>
                <button type="submit" disabled={loading || !!lockoutMsg}
                  className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-gray-600 text-white font-semibold py-2.5 rounded-lg transition-colors"
                  data-testid="wms-login-submit">
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>
              </form>
            </>
          ) : (
            <>
              <h2 className="text-xl font-semibold text-white mb-2 flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-blue-300" />Two-Factor Authentication
              </h2>
              <p className="text-blue-300 text-sm mb-6">Enter the 6-digit code from your authenticator app</p>
              {error && <div className="bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-2 rounded-lg mb-4 text-sm">{error}</div>}
              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <label className="text-sm text-blue-200 mb-1.5 block">Authenticator Code</label>
                  <input type="text" maxLength={6} value={totp} onChange={e => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white text-center text-2xl font-mono tracking-[0.5em] placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    placeholder="000000" autoFocus />
                </div>
                <button type="submit" disabled={loading || totp.length !== 6}
                  className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-gray-600 text-white font-semibold py-2.5 rounded-lg transition-colors">
                  {loading ? 'Verifying...' : 'Verify Code'}
                </button>
                <button type="button" onClick={() => { setStep('credentials'); setTotp(''); setError(''); }}
                  className="w-full text-blue-300 text-sm hover:text-white transition-colors">
                  ← Back to login
                </button>
              </form>
            </>
          )}
          
        </div>

        {/* Getting Started / 2FA Setup Guide */}
        <div className="mt-4 bg-white/5 backdrop-blur rounded-2xl border border-white/10 overflow-hidden">
          <button onClick={() => { setShowGuide(v => !v); if (!showGuide) loadQR(); }}
            className="w-full flex items-center justify-between px-5 py-3.5 text-left">
            <span className="flex items-center gap-2 text-sm text-blue-200">
              <Smartphone className="w-4 h-4" />
              New user? How to set up Google Authenticator (2FA)
            </span>
            {showGuide ? <ChevronDown className="w-4 h-4 text-blue-300" /> : <ChevronRight className="w-4 h-4 text-blue-300" />}
          </button>

          {showGuide && (
            <div className="px-5 pb-5 space-y-4 border-t border-white/10 pt-4">
              <p className="text-xs text-blue-300/80 leading-relaxed">
                Two-Factor Authentication (2FA) adds a second layer of security. After setting it up,
                you'll need your phone to log in.
              </p>

              {/* Step 1 */}
              <div className="flex gap-3">
                <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">1</div>
                <div>
                  <p className="text-sm font-medium text-white">Install Google Authenticator</p>
                  <p className="text-xs text-blue-300/70 mt-0.5">Download from the App Store (iOS) or Google Play (Android). Search "Google Authenticator".</p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex gap-3">
                <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">2</div>
                <div>
                  <p className="text-sm font-medium text-white">Log in and enable 2FA</p>
                  <p className="text-xs text-blue-300/70 mt-0.5">
                    After logging in → click <strong className="text-blue-200">Security</strong> in the left sidebar → click <strong className="text-blue-200">"Enable 2FA"</strong> button.
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex gap-3">
                <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">3</div>
                <div>
                  <p className="text-sm font-medium text-white">Scan the QR code</p>
                  <p className="text-xs text-blue-300/70 mt-0.5">Open Google Authenticator → tap "+" → tap "Scan QR code" → point your camera at the QR code shown.</p>
                  {/* Live QR code for demo account */}
                  {qrData && !qrData.error && qrData.qr_code_base64 && (
                    <div className="mt-3">
                      <div className="inline-block bg-white p-2 rounded-xl shadow-lg">
                        <img src={`data:image/png;base64,${qrData.qr_code_base64}`} alt="2FA QR Code"
                          className="w-28 h-28" />
                      </div>
                      {qrData.secret && (
                        <div className="mt-2 bg-white/10 rounded-lg px-3 py-2">
                          <p className="text-[9px] text-blue-300/70 mb-0.5">Manual entry key:</p>
                          <p className="text-[10px] text-blue-200 font-mono break-all select-all">{qrData.secret}</p>
                        </div>
                      )}
                      {qrData.already_enabled && (
                        <p className="text-[10px] text-emerald-400 mt-2">2FA is active — share this QR with others who need access</p>
                      )}
                    </div>
                  )}
                  {qrLoading && (
                    <div className="mt-3 w-28 h-28 bg-white/10 rounded-xl animate-pulse flex items-center justify-center">
                      <p className="text-[10px] text-blue-300">Loading QR...</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex gap-3">
                <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">4</div>
                <div>
                  <p className="text-sm font-medium text-white">Confirm with the 6-digit code</p>
                  <p className="text-xs text-blue-300/70 mt-0.5">Enter the 6-digit code from the app to activate 2FA. Next time you log in, you'll be prompted for this code.</p>
                </div>
              </div>

              <div className="bg-blue-500/10 border border-blue-400/30 rounded-lg p-3 mt-2">
                <p className="text-xs text-blue-200">
                  Scan the QR above → log in → go to <strong>Security → Confirm 2FA</strong> with the 6-digit code from the app.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Support contact */}
        <p className="text-center text-xs text-gray-400 mt-6">
          Need help?{' '}
          <a href="mailto:support@agora-sg.com"
            className="text-blue-400 hover:text-blue-300 font-medium inline-flex items-center gap-1"
            data-testid="login-support-email">
            <Mail className="w-3 h-3" />support@agora-sg.com
          </a>
        </p>
      </div>
    </div>
  );
}
